import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { arrayToObject, getUrlWithParams } from 'core-components/helpers/url'
import { NotificationController } from 'core-components/library/popup'
import { Controller, ValidationStatus, Validator } from 'core-components/library/validation'

import { ERROR_MESSAGE } from '../../constants/validation'
import { IBaseTabButtonsProps } from '../../models/IBaseTabButtons'
import { ITabProps, ITabValidators } from '../../models/ITab'
import { Tabs } from '../Tabs'

import styles from './BaseTabButtons.module.scss'

export const BaseTabButtons = ({
  className,
  classes,
  items,
  defaultValue,
  urlKey,
  onChange,
  value,
  titleRender,
  contentAlwaysExisting,
  additionalHeaderProps,
  headerContent,
  headerTemplate,
  urlKeyHandler,
  isTabChangeDisabled = false,
}: IBaseTabButtonsProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const visibleTabs = items.filter((tab) => tab.viewCond !== false)
  const [tabsValidators, setTabsValidators] = useState<ITabValidators>({})

  const getUrlKey = (urlKey: string) => {
    const urlObj = arrayToObject(location.pathname.split('/').filter(Boolean))
    return urlObj[urlKey]
  }

  const changeTab = (newValue: string) => {
    const callOnChange = (newValue: string) => onChange && onChange(newValue)
    if (newValue === activeTab || isTabChangeDisabled) {
      return
    }
    if (defaultValue) {
      activateTab(newValue)
      return
    }
    if (urlKey) {
      const processUrlChange = () => {
        const url = getUrlWithParams({ [urlKey]: newValue })
        navigate(url)
        activateTab(newValue)
        callOnChange(newValue)
      }
      if (urlKeyHandler) {
        urlKeyHandler()
          .then(() => {
            processUrlChange()
          })
          .catch((e) => {
            NotificationController.showError({
              message: `Changing tab went wrong: ${e || e?.message || ''}`,
            })
            console.error('TabButtons async operation: ' + e)
          })
      } else {
        processUrlChange()
      }
      return
    }
    if (value) {
      callOnChange(newValue)
      if (!onChange) {
        const name = 'Core-components/library/tabButtons'
        const message = 'If you set value property, you must set onChange property too'
        console.error(`${name}: ${message}`)
      }
      return
    }
    activateTab(newValue)
  }

  const calculateKey = (newValue: string) => {
    return newValue || defaultValue || visibleTabs[0].id
  }

  const [activeTab, setActiveTab] = useState(() => {
    const initialValue = urlKey ? getUrlKey(urlKey) : value
    return calculateKey(initialValue)
  })

  const renderedTabsRef = useRef<string[]>([activeTab])

  const activateTab = (id: string) => {
    setActiveTab(id)
    if (contentAlwaysExisting) {
      renderedTabsRef.current.push(id)
    } else {
      renderedTabsRef.current = [id]
    }
  }

  useEffect(() => {
    if (value && !urlKey) {
      activateTab(value)
    }
  }, [value])

  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.find((t) => t.id === activeTab)) {
      const newId = visibleTabs[0].id
      if (urlKey) {
        const newUrl = getUrlWithParams({ [urlKey]: newId })
        navigate(newUrl)
      } else {
        changeTab(newId)
      }
    }
  }, [visibleTabs, urlKey, navigate])

  useEffect(() => {
    const checkUrl = () => {
      if (urlKey) {
        const key = getUrlKey(urlKey)
        changeTab(calculateKey(key))
      }
    }
    checkUrl()
  }, [location])

  const getContent = (isActive: boolean, tab: ITabProps) => {
    return (
      <div
        key={`tab-${tab.id}`}
        role={'tabpanel'}
        id={`baseTabButtons-panel-${tab.id}`}
        aria-labelledby={`baseTabButtons-tab-${tab.id}`}
        tabIndex={0}
        className={classNames(styles.content, classes?.content, {
          ['content-hidden']: !isActive,
        })}
        data-testid={'baseTabButtons-content'}
      >
        {tab.content()}
      </div>
    )
  }

  const renderHeader = () => {
    if (headerTemplate) {
      return headerTemplate({
        activeTab,
        onChangeTab: changeTab,
        tabsValidators,
        ...additionalHeaderProps,
      })
    }
    return (
      <div
        className={classNames(styles.header, classes?.header)}
        role={'tablist'}
        data-testid={'baseTabButtons-header'}
      >
        <Tabs
          items={items}
          activeTab={activeTab}
          onChangeTab={changeTab}
          tabsValidators={tabsValidators}
          titleRender={titleRender}
          classes={classes}
          contentAlwaysExisting={contentAlwaysExisting}
          headerContent={headerContent}
          {...additionalHeaderProps}
        />
      </div>
    )
  }

  return (
    <div
      className={classNames(styles.root, classes?.root, className)}
      data-testid={'baseTabButtons-root'}
    >
      {renderHeader()}

      {visibleTabs.map((tab) => {
        const isActive = tab.id === activeTab
        if (renderedTabsRef.current.includes(tab.id)) {
          if (!contentAlwaysExisting) {
            return getContent(isActive, tab)
          }
          return (
            <Validator
              key={`tabContent-${tab.id}`}
              value={tabsValidators[tab.id] + ''}
              tooltipVisible={false}
              checkFn={async (value) => {
                return value !== 'true' ? ERROR_MESSAGE : ''
              }}
            >
              <Controller
                onValidationStatusChange={({ status }) => {
                  setTabsValidators((prev) => {
                    return {
                      ...prev,
                      [tab.id]: status === ValidationStatus.Valid,
                    }
                  })
                }}
              >
                {getContent(isActive, tab)}
              </Controller>
            </Validator>
          )
        }
        return null
      })}
    </div>
  )
}
